import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  CheckCircle2,
  ClipboardCheck,
  FolderKanban,
  Layers3,
  Plus,
  Search,
} from "lucide-react";
import { CommandHero } from "@/components/shell/CommandHero";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { EmptyState } from "@/components/common/EmptyState";
import { ProjectCard } from "@/components/content/ProjectCard";
import { useAppState } from "@/state/AppStateProvider";
import { buildProjectsWorkspace } from "@/lib/projectWorkspace";

export default function Projects() {
  const { state } = useAppState();
  const [tab, setTab] = useState("all");
  const [q, setQ] = useState("");

  const workspace = useMemo(() => (
    buildProjectsWorkspace({
      projects: state.projects,
      assets: state.assets,
      calendarItems: state.calendar,
      inbox: state.inbox,
      runs: state.runs,
      filters: { tab, query: q },
    })
  ), [state.projects, state.assets, state.calendar, state.inbox, state.runs, tab, q]);

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-6 pb-20" data-testid="projects-page">
      <CommandHero
        dark
        eyebrow="Projects"
        title="Campaign hubs for every content push."
        description="Track the assets, sources, scheduled posts, and agent runs that belong to each campaign, offer, course, or recurring content series."
        testId="projects-command-hero"
        visual={<ProjectsHeroVisual workspace={workspace} />}
      />

      <section className="rounded-xl border bg-card p-3 inner-highlight" style={{ borderColor: "var(--border-subtle)" }}>
        <div className="flex flex-wrap items-center gap-3">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="flex h-auto flex-wrap justify-start gap-1 bg-transparent p-0" data-testid="projects-tabs">
              {workspace.tabs.map((item) => (
                <TabsTrigger key={item.id} value={item.id} className="gap-1.5 data-[state=active]:shadow-sm" data-testid={`projects-tab-${item.id}`}>
                  {item.label}
                  <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground">{workspace.lanes?.find((l) => l.id === item.id)?.count ?? 0}</span>
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          <div className="relative w-full sm:ml-auto sm:max-w-sm">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={q}
              onChange={(event) => setQ(event.target.value)}
              placeholder="Search projects, pillars, platforms..."
              className="h-9 pl-8"
              data-testid="projects-search"
              style={{ backgroundColor: "var(--surface-2)", borderColor: "var(--border-subtle)" }}
            />
          </div>
        </div>
      </section>

      {workspace.filtered.length === 0 ? (
        <EmptyState
          icon={FolderKanban}
          title="No projects found"
          description="Try a different filter, or kick off a campaign from Create."
          action={<Button asChild><Link to="/create">New project</Link></Button>}
        />
      ) : (
        <section className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="projects-grid">
          {workspace.filtered.map((project) => <ProjectCard key={project.id} project={project} />)}
        </section>
      )}
    </motion.div>
  );
}

function ProjectsHeroVisual({ workspace }) {
  const items = [
    { label: "Projects", value: workspace.summary.total, icon: FolderKanban, color: "text-[color:var(--accent-cyan)]", bg: "bg-[color:var(--accent-blue)]/12" },
    { label: "Needs review", value: workspace.summary.needsReview, icon: ClipboardCheck, color: "text-amber-600", bg: "bg-[color:var(--accent-amber)]/12" },
    { label: "Approved", value: workspace.summary.approved, icon: CheckCircle2, color: "text-[color:var(--accent-green)]", bg: "bg-[color:var(--accent-green)]/12" },
    { label: "Assets", value: workspace.summary.assets, icon: Layers3, color: "text-[color:var(--accent-green)]", bg: "bg-[color:var(--accent-green)]/12" },
  ];

  return (
    <div className="space-y-3" data-testid="projects-hero-summary">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-foreground">Campaign snapshot</p>
          <p className="text-[12px] text-muted-foreground">Where your bigger content pushes stand.</p>
        </div>
        <Button asChild size="sm" className="gap-1.5" style={{ backgroundColor: "var(--accent-blue)", color: "#FFFFFF" }} data-testid="projects-new">
          <Link to="/create"><Plus className="h-3.5 w-3.5" /> New</Link>
        </Button>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {items.map(({ label, value, icon: Icon, color, bg }) => (
          <div key={label} className="rounded-md border bg-card p-3" style={{ borderColor: "var(--border-subtle)" }}>
            <div className="flex items-center justify-between gap-2">
              <span className={`flex h-7 w-7 items-center justify-center rounded-md ${bg}`}>
                <Icon className={`h-3.5 w-3.5 ${color}`} />
              </span>
              <span className="text-lg font-bold tracking-[-0.03em] text-foreground">{value}</span>
            </div>
            <p className="mt-2 text-[11.5px] font-medium text-muted-foreground">{label}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
